import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Lock,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { FadeUp } from "@/components/Reveal";

const CONTENT = {
  en: {
    banner: "This form is confidential. Your details are encrypted in transit, stored securely, and used only to arrange a first conversation — never shared, never added to any list.",
    eyebrow: "Pre-consultation inquiry",
    h1: "Let's start gently.",
    sub: "A few short questions — about two minutes. There are no wrong answers.",
    steps: ["About you", "What brings you", "Practicalities", "Privacy & send"],
    firstName: "First name", firstNamePh: "Your first name",
    lastName: "Last name", lastNamePh: "Your last name",
    email: "Email", emailPh: "you@example.com",
    phone: "Phone (optional)", phonePh: "Only if you'd like a call back",
    bringingLabel: "What's bringing you here?",
    bringingHint: "(choose any)",
    reasons: ["Anxiety & overwhelm", "Low mood & depression", "Relationships", "Work & burnout", "Grief & loss", "Life transitions", "Self-esteem", "Feeling stuck", "Other"],
    otherLabel: "Other",
    otherInputLabel: "A few words about the 'other' area",
    otherInputPh: "Whatever feels right to name — or close enough",
    detailLabel: "Anything you'd like me to know? (optional)",
    detailPh: "A sentence or two is plenty. You don't have to explain everything here.",
    supportLabel: "Type of support",
    supportPh: "What are you looking for?",
    supportNote: "'Not sure' is a perfectly good answer — we can work it out together on the call.",
    supportOptions: [
      { value: "individual", label: "Individual therapy (open-ended)" },
      { value: "short-term", label: "Short-term consultation" },
      { value: "unsure", label: "Not sure yet" },
    ],
    availLabel: "Preferred availability",
    availPh: "When are you usually free? Days, times, and your time zone. Rough is fine.",
    reviewTitle: "Your enquiry",
    rows: { name: "Name", email: "Email", support: "Support", format: "Format", formatValue: "Online", availability: "Availability", areas: "Areas" },
    consent: "I understand this enquiry is confidential, that my details will be stored securely and used only to arrange a first conversation, and that nothing is shared with third parties.",
    back: "Back", next: "Continue", submit: "Send enquiry", submitting: "Sending…",
    successH1: "Thank you",
    successBody: (email) => `Your enquiry has been received, safely and privately. I'll reply to ${email} personally, usually within a couple of days, to arrange your free introductory call.`,
    successBtn: "Back to home",
    errors: {
      name: "Please share your name.",
      email: "Please enter a valid email address.",
      reasons: "Please choose at least one area.",
      other: "Please briefly describe the 'other' area.",
      support: "Please choose the type of support you're looking for.",
      availability: "Please note when you'd usually be free.",
      consent: "Please confirm you've read the privacy note.",
    },
  },
  el: {
    banner: "Η φόρμα αυτή είναι εμπιστευτική. Τα στοιχεία σας κρυπτογραφούνται κατά τη μεταφορά, αποθηκεύονται με ασφάλεια και χρησιμοποιούνται μόνο για να κανονίσουμε μια πρώτη κουβέντα — δεν κοινοποιούνται ποτέ, δεν μπαίνουν σε λίστες.",
    eyebrow: "Αίτημα γνωριμίας",
    h1: "Ας ξεκινήσουμε.",
    sub: "Λίγες σύντομες ερωτήσεις — περίπου δύο λεπτά. Δεν υπάρχουν λάθος απαντήσεις.",
    steps: ["Για εσάς", "Θέματα", "Πρακτικά", "Αποστολή"],
    firstName: "Όνομα", firstNamePh: "Το μικρό σας όνομα",
    lastName: "Επώνυμο", lastNamePh: "Το επώνυμό σας",
    email: "Email", emailPh: "you@example.com",
    phone: "Τηλέφωνο (προαιρετικό)", phonePh: "Μόνο αν θέλετε τηλεφωνική επαφή",
    bringingLabel: "Τι σας φέρνει εδώ;",
    bringingHint: "(επιλέξτε όσα θέλετε)",
    reasons: ["Άγχος & φορτισμένη καθημερινότητα", "Καταθλιπτική διάθεση", "Σχέσεις", "Δουλειά & burnout", "Πένθος & απώλεια", "Μεταβάσεις ζωής", "Αυτοεκτίμηση", "Αίσθηση αδιέξοδου", "Άλλο"],
    otherLabel: "Άλλο",
    otherInputLabel: "Μερικά λόγια για το «άλλο»",
    otherInputPh: "Ό,τι σας βολεύει να ονομάσετε — ή κάτι κοντά σε αυτό",
    detailLabel: "Θα θέλατε να μου πείτε κάτι παραπάνω; (προαιρετικό)",
    detailPh: "Μια-δύο φράσεις αρκούν. Δεν χρειάζεται να τα πείτε όλα εδώ.",
    supportLabel: "Τύπος υποστήριξης",
    supportPh: "Τι ψάχνετε;",
    supportNote: "Το «δεν είμαι σίγουρος/ή» είναι μια απολύτως καλή απάντηση — θα το δούμε μαζί στην κλήση.",
    supportOptions: [
      { value: "individual", label: "Ατομική ψυχοθεραπεία (ανοιχτής διάρκειας)" },
      { value: "short-term", label: "Σύντομη συμβουλευτική" },
      { value: "unsure", label: "Δεν είμαι σίγουρος/ή" },
    ],
    availLabel: "Προτιμώμενες ώρες",
    availPh: "Πότε είστε συνήθως ελεύθεροι/ές; Μέρες, ώρες, και η ζώνη ώρας σας. Ένα πρόχειρο αρκεί.",
    reviewTitle: "Το αίτημά σας",
    rows: { name: "Όνομα", email: "Email", support: "Υποστήριξη", format: "Μορφή", formatValue: "Online", availability: "Διαθεσιμότητα", areas: "Θέματα" },
    consent: "Κατανοώ ότι το αίτημα αυτό είναι εμπιστευτικό, ότι τα στοιχεία μου αποθηκεύονται με ασφάλεια και χρησιμοποιούνται μόνο για να κανονιστεί μια πρώτη κουβέντα, και ότι τίποτα δεν κοινοποιείται σε τρίτους.",
    back: "Πίσω", next: "Συνέχεια", submit: "Αποστολή αιτήματος", submitting: "Αποστολή…",
    successH1: "Ευχαριστώ",
    successBody: (email) => `Το αίτημά σας ελήφθη — με ασφάλεια και απόλυτα ιδιωτικά. Θα σας απαντήσω προσωπικά στο ${email}, συνήθως μέσα σε δύο εργάσιμες ημέρες, για να κανονίσουμε τη δωρεάν γνωριμία.`,
    successBtn: "Επιστροφή στην αρχική",
    errors: {
      name: "Παρακαλώ συμπληρώστε το όνομά σας.",
      email: "Παρακαλώ συμπληρώστε ένα έγκυρο email.",
      reasons: "Παρακαλώ επιλέξτε τουλάχιστον ένα θέμα.",
      other: "Παρακαλώ περιγράψτε σύντομα το «άλλο».",
      support: "Παρακαλώ επιλέξτε τον τύπο υποστήριξης.",
      availability: "Παρακαλώ σημειώστε πότε είστε συνήθως ελεύθεροι/ές.",
      consent: "Παρακαλώ επιβεβαιώστε ότι διαβάσατε την επισήμανση απορρήτου.",
    },
  },
};

const Intake = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];
  useSeo("intake");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    reasons: [], reasons_other: "", reasons_detail: "",
    support_type: "", availability: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleReason = (r) =>
    setForm((f) => ({
      ...f,
      reasons: f.reasons.includes(r) ? f.reasons.filter((x) => x !== r) : [...f.reasons, r],
    }));

  const validate = (s) => {
    if (s === 0) {
      if (!form.first_name.trim() || !form.last_name.trim()) return c.errors.name;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return c.errors.email;
    }
    if (s === 1 && form.reasons.length === 0) return c.errors.reasons;
    if (s === 1 && form.reasons.includes(c.otherLabel) && !form.reasons_other.trim()) return c.errors.other;
    if (s === 2) {
      if (!form.support_type) return c.errors.support;
      if (!form.availability.trim()) return c.errors.availability;
    }
    if (s === 3 && !form.consent) return c.errors.consent;
    return null;
  };

  const next = () => {
    const err = validate(step);
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async () => {
    const err = validate(3);
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const reasons = [...form.reasons];
      let detail = form.reasons_detail.trim();
      if (form.reasons.includes(c.otherLabel) && form.reasons_other.trim()) {
        detail = `${detail ? detail + "\n\n" : ""}${c.otherLabel}: ${form.reasons_other.trim()}`;
      }
      await api.post("/intake", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || null,
        referral: null,
        reasons,
        reasons_detail: detail || null,
        format: "online",
        support_type: form.support_type,
        availability: form.availability,
        consent: form.consent,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="container-x flex min-h-[80vh] items-center justify-center py-32" data-testid="intake-success">
        <FadeUp>
          <div className="mx-auto max-w-xl text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-sage-500" strokeWidth={1.25} />
            <h1 className="mt-8 font-display text-4xl font-light tracking-tight text-sage-900 sm:text-5xl">
              {c.successH1}, {form.first_name}.
            </h1>
            <p className="mt-6 leading-relaxed text-muted-foreground">{c.successBody(form.email)}</p>
            <Button
              asChild
              data-testid="intake-success-home"
              className="mt-10 rounded-full bg-sage-600 px-8 py-6 text-background hover:bg-sage-700"
            >
              <Link to="/">{c.successBtn}</Link>
            </Button>
          </div>
        </FadeUp>
      </div>
    );
  }

  return (
    <div className="container-x max-w-2xl pb-24 pt-32 md:pt-40 lg:pt-44" data-testid="page-intake">
      <FadeUp>
        <div className="grain-card flex items-start gap-3 bg-secondary/70 p-5">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-sage-600" />
          <p className="text-xs leading-relaxed text-muted-foreground">{c.banner}</p>
        </div>

        <p className="eyebrow mt-12 mb-4">{c.eyebrow}</p>
        <h1 className="font-display text-4xl font-light leading-tight tracking-tight text-sage-900 sm:text-5xl">
          {c.h1}
        </h1>
        <p className="mt-4 leading-relaxed text-muted-foreground">{c.sub}</p>
      </FadeUp>

      {/* progress */}
      <div className="mt-10 flex items-center gap-2" data-testid="intake-progress">
        {c.steps.map((label, i) => (
          <div key={label} className="flex flex-1 flex-col gap-2">
            <div
              className={`h-1 rounded-full transition-colors duration-300 ${
                i < step ? "bg-sage-500" : i === step ? "bg-sage-700" : "bg-sage-600/15"
              }`}
            />
            <span className={`text-[11px] tracking-wide ${i === step ? "text-sage-700" : "text-muted-foreground"}`}>
              {label}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-10 min-h-[380px]" data-testid="intake-step-panel">
        {step === 0 && (
          <div className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first-name">{c.firstName}</Label>
                <Input id="first-name" data-testid="intake-first-name" value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)} placeholder={c.firstNamePh}
                  className="rounded-xl border-sage-600/20 bg-card" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">{c.lastName}</Label>
                <Input id="last-name" data-testid="intake-last-name" value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)} placeholder={c.lastNamePh}
                  className="rounded-xl border-sage-600/20 bg-card" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{c.email}</Label>
              <Input id="email" type="email" data-testid="intake-email" value={form.email}
                onChange={(e) => set("email", e.target.value)} placeholder={c.emailPh}
                className="rounded-xl border-sage-600/20 bg-card" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{c.phone}</Label>
              <Input id="phone" type="tel" data-testid="intake-phone" value={form.phone}
                onChange={(e) => set("phone", e.target.value)} placeholder={c.phonePh}
                className="rounded-xl border-sage-600/20 bg-card" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-8">
            <div className="space-y-3">
              <Label>
                {c.bringingLabel} <span className="text-muted-foreground">{c.bringingHint}</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {c.reasons.map((r) => {
                  const active = form.reasons.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      data-testid={`intake-reason-chip-${r.toLowerCase().replace(/[^a-z\u0370-\u03ff]+/g, "-")}`}
                      aria-pressed={active}
                      onClick={() => toggleReason(r)}
                      className={`rounded-full border px-4 py-2.5 text-sm transition-colors duration-200 ${
                        active
                          ? "border-sage-600 bg-sage-600 text-background"
                          : "border-sage-600/20 bg-card text-foreground/80 hover:border-sage-600/50"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>
            {form.reasons.includes(c.otherLabel) && (
              <div className="space-y-2">
                <Label htmlFor="reasons-other">{c.otherInputLabel}</Label>
                <Input id="reasons-other" data-testid="intake-reasons-other" value={form.reasons_other}
                  onChange={(e) => set("reasons_other", e.target.value)} placeholder={c.otherInputPh}
                  className="rounded-xl border-sage-600/20 bg-card" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="reasons-detail">{c.detailLabel}</Label>
              <Textarea id="reasons-detail" data-testid="intake-reasons-detail" value={form.reasons_detail}
                onChange={(e) => set("reasons_detail", e.target.value)} placeholder={c.detailPh} rows={4}
                className="rounded-xl border-sage-600/20 bg-card" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8">
            <div className="space-y-2">
              <Label>{c.supportLabel}</Label>
              <Select value={form.support_type} onValueChange={(v) => set("support_type", v)}>
                <SelectTrigger data-testid="intake-support-type" className="w-full rounded-xl border-sage-600/20 bg-card">
                  <SelectValue placeholder={c.supportPh} />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-sage-600/15 bg-card">
                  {c.supportOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`intake-support-type-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs leading-relaxed text-muted-foreground">{c.supportNote}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="availability">{c.availLabel}</Label>
              <Textarea id="availability" data-testid="intake-availability" value={form.availability}
                onChange={(e) => set("availability", e.target.value)} placeholder={c.availPh} rows={3}
                className="rounded-xl border-sage-600/20 bg-card" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8">
            <div className="grain-card space-y-4 bg-card p-6" data-testid="intake-review">
              <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-sage-600">
                <ShieldCheck className="h-4 w-4 text-clay" /> {c.reviewTitle}
              </p>
              {[
                [c.rows.name, `${form.first_name} ${form.last_name}`.trim()],
                [c.rows.email, form.email],
                [c.rows.support, c.supportOptions.find((o) => o.value === form.support_type)?.label || "—"],
                [c.rows.format, c.rows.formatValue],
                [c.rows.availability, form.availability],
                [c.rows.areas, form.reasons.join(", ") || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col gap-0.5 border-t border-sage-600/10 pt-3 text-sm sm:flex-row sm:gap-6">
                  <span className="w-28 shrink-0 text-muted-foreground">{k}</span>
                  <span className="whitespace-pre-line text-foreground/90">{v || "—"}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-3">
              <Checkbox id="consent" data-testid="intake-consent" checked={form.consent}
                onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5" />
              <Label htmlFor="consent" className="text-sm font-normal leading-relaxed text-foreground/85">
                {c.consent}
              </Label>
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 flex items-center justify-between border-t border-sage-600/10 pt-8">
        <Button
          variant="ghost"
          data-testid="intake-back"
          onClick={() => setStep((s) => Math.max(s - 1, 0))}
          disabled={step === 0}
          className="rounded-full text-muted-foreground disabled:opacity-0"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> {c.back}
        </Button>
        {step < 3 ? (
          <Button data-testid="intake-next" onClick={next} className="rounded-full bg-sage-600 px-8 text-background hover:bg-sage-700">
            {c.next} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button data-testid="intake-submit-button" onClick={submit} disabled={submitting}
            className="rounded-full bg-sage-600 px-8 text-background hover:bg-sage-700">
            {submitting ? c.submitting : c.submit} {!submitting && <Check className="ml-2 h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Intake;
